import { Box } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { CategoryIcon } from '../../components/MachineTable/MachineIcon'
import { getControl } from '../../store/ControlById/selectors'
import { PartLog } from '../../types'
import { hexPinCode, toBitPins, toHms } from '../../utils/businessUtil'
import { dicebearUrl } from '../../utils/dicebear'

export type Props = {
	plog: PartLog
}

const Field = ({
	label,
	children,
}: React.PropsWithChildren<{ label: string }>) => (
	<Box
		sx={{
			display: 'inline-flex',
			gap: 0.5,
			alignItems: 'baseline',
			fontSize: 11,
			color: 'text.secondary',
			fontVariantNumeric: 'tabular-nums',
		}}
	>
		<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
			{label}
		</Box>
		<span>{children}</span>
	</Box>
)

const BigPinView = ({ pin }: { pin?: number }) => {
	if (typeof pin !== 'number') return <span>-</span>
	return (
		<Box
			sx={{
				display: 'flex',
				gap: 0.5,
				alignItems: 'center',
				fontFamily: 'monospace',
				flexWrap: 'wrap',
				minWidth: 0,
			}}
		>
			<span>[{hexPinCode(pin)}]</span>
			<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
				{toBitPins(pin).map((b, i) => (
					<Box
						key={i}
						component="span"
						sx={{
							px: '2px',
							borderRadius: '2px',
							bgcolor: b ? 'warning.light' : 'transparent',
						}}
					>
						{b ? '1' : '0'}
					</Box>
				))}
			</Box>
		</Box>
	)
}

export default function PinLogCard({ plog }: Props) {
	const control = useSelector((s) => getControl(s, plog.alog.controlId))
	const a = plog.alog

	return (
		<Box
			className="pin-log-card"
			data-tag={a.tag}
			data-cancelled={a.cancelled}
			data-progress={a.progress}
			data-timing={plog.timing}
			sx={{
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1,
				p: 1,
				my: 1,
				display: 'flex',
				flexWrap: 'wrap',
				alignItems: 'center',
				gap: 1,
				opacity: a.cancelled ? 0.55 : 1,
			}}
		>
			<Field label="P">{toHms(plog.time)}</Field>
			<Box
				component="img"
				src={dicebearUrl(a.id)}
				alt={plog.id}
				sx={{
					width: 22,
					height: 22,
					borderRadius: 0.5,
					bgcolor: 'action.hover',
					flexShrink: 0,
				}}
			/>
			<CategoryIcon
				category={control.category}
				stepRate={6}
				style={{ width: '22px', height: '22px', flexShrink: 0 }}
			/>
			<BigPinView pin={plog.pin} />
			<Field label="run">{a.runningTime}s</Field>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
				<Field label="S">{toHms(plog.scheduleTime)}</Field>
				<Field label="C">{toHms(a.createdAt)}</Field>
				<Field label="U">{toHms(a.updatedAt)}</Field>
			</Box>
		</Box>
	)
}
